import axios from 'axios';
import { SetAuthHeader } from './utils/authHeader';
import { useOktaAuth } from './use-okta-auth';
import { HandleAxiosError } from './utils/axiosError';
import { createLocalStorageSync } from '../components/utilities/functions';
import { captureException } from '@sentry/gatsby';

const pellaAPIURL = process.env.GATSBY_PELLA_API;
const environment = process.env.GATSBY_CONTENTSTACK_ENVIRONMENT;

let params = {};

export const useCheckout = () => {
	const { authState } = useOktaAuth();
	const accessToken = authState?.accessToken?.accessToken;
	const cartSync = createLocalStorageSync('cartSession');

	const requestConfig = {
		headers: {
			...SetAuthHeader(accessToken),
		},
		withCredentials: true,
	};

	const updateCustomer = async (customerID, firstname, lastname, primaryAddressID = null) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const body = {
				firstname: firstname,
				lastname: lastname,
			};

			if (primaryAddressID !== null) {
				body.primary_address = primaryAddressID;
			}

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.put(`${pellaAPIURL}/web-cart-order-mgmt/v1/customers/${customerID}`, body, { ...requestConfig, params: params });

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const createAddress = async (customerID, addressData) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.post(`${pellaAPIURL}/web-cart-order-mgmt/v1/customers/${customerID}/addresses`, addressData, { ...requestConfig, params: params });

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const getAddress = async (customerID, addressID) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.get(`${pellaAPIURL}/web-cart-order-mgmt/v1/customers/${customerID}/addresses/${addressID}`, { ...requestConfig, params: params });

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const updateAddress = async (customerID, addressID, addressData) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.put(`${pellaAPIURL}/web-cart-order-mgmt/v1/customers/${customerID}/addresses/${addressID}`, addressData, { ...requestConfig, params: params });

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const getTaxEstimate = async (shipTo, shippingCost = 0, transactionDate, productionSpeed = 0) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const body = {
				ship_to_address: {
					...shipTo,
					line_1: shipTo?.street,
				},
				shipping_cost: shippingCost,
				manufacture_cost: productionSpeed,
			};

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.post(`${pellaAPIURL}/web-ecommerce/api/v1/carts/tax${transactionDate && environment !== 'production' ? '?transaction_date=' + transactionDate : ''}`, body, { ...requestConfig, params: params });
			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const addPromotion = async (promocode) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.post(`${pellaAPIURL}/web-ecommerce/api/v1/carts/promotions`, { code: promocode }, { ...requestConfig, params: params });

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const deletePromotion = async (promocode) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.delete(`${pellaAPIURL}/web-ecommerce/api/v1/carts/promotions/${promocode}`, { ...requestConfig, params: params });

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const checkoutCart = async (quoteID, customerID, clientID, jobsiteAddress, pickup_address = null, payment_token, order_type = 'NonInstall', mobileNumber, transactionDate, manufacture_method) => {
		try {
			const userPostalCode = localStorage?.getItem('UserPostalCode');

			let data = {
				customer: { id: customerID },
				clientID: clientID,
				shipping_address: jobsiteAddress,
				install_address: jobsiteAddress,
				payment_token: payment_token,
				order_type: order_type,
			};

			if (pickup_address) {
				data = {
					...data,
					pickup_address: pickup_address,
				};
			}

			if (mobileNumber) {
				data = {
					...data,
					notifications: [
						{
							type: 'transactional',
							phone: mobileNumber,
						},
					],
				};
			}

			if (manufacture_method) {
				data.manufacture_method = manufacture_method.selected.method;
			}

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.post(`${pellaAPIURL}/web-cart-order-mgmt/v1/carts/${quoteID}/checkout${transactionDate && environment !== 'production' ? '?transaction_date=' + transactionDate : ''}`, data, { ...requestConfig, params: params });

			if (!response?.data?.Order?.id) {
				const error = new Error('Order response invalid');
				captureException(error, {
					extra: {
						response: response?.data,
						params,
					},
				});
			}

			if (!response?.error) {
				cartSync.set({});
			}

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const getPayment = async (customerID) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.get(`${pellaAPIURL}/web-cart-order-mgmt/v1/customers/${customerID}/payment-instrument`, { ...requestConfig, params: params });

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const addPaypalPaymentMethod = async (customerID, shippingAddress, orderTotal) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const data = {
				payment_type: 'paypal',
				shipping_address: shippingAddress,
				order_total: orderTotal,
				order_currency: 'USD',
			};

			// this api requires line_2 to be null, should probably be fixed on the backend
			shippingAddress.line_2 = null;

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.post(`${pellaAPIURL}/web-cart-order-mgmt/v1/customers/${customerID}/payment-methods`, data, { ...requestConfig, params: params });

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const recaptcha = async (guid) => {
		try {
			const response = await axios.get(`${process.env.GATSBY_PAYMENT_TOKEN_URL}/api/payment/verify/${guid}`);

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const createPaymentToken = async (customerID, customerEmail, paymentType, card, billingAddress, jobsiteAddress, guid, captchaToken) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const data = {
				payment_type: paymentType,
				card: card,
				billing_address: billingAddress,
				shipping_address: jobsiteAddress,
				email: customerEmail,
				guid: guid,
				recaptcha_response: captchaToken,
			};

			const response = await axios.post(`${process.env.GATSBY_PAYMENT_TOKEN_URL}/api/payment/tokenize`, data);

			if (response.data?.error) {
				return response.data;
			} else {
				const requestConfig = {
					headers: {
						...SetAuthHeader(accessToken),
					},
					withCredentials: true,
				};
				const newData = {
					payment_type: response.data.type,
					card: {
						name: response.data.card.name,
						token: response.data.id,
					},
				};

				if (userPostalCode) {
					params.client_postal_code = userPostalCode;
				}

				const saveToken = await axios.post(`${pellaAPIURL}/web-cart-order-mgmt/v1/customers/${customerID}/payment-methods`, newData, { ...requestConfig, params: params });

				return saveToken.data;
			}
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const getDeliveryOptions = async (job_site_address) => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			const data = {
				job_site_address: {
					line_1: job_site_address.line_1,
					city: job_site_address.city,
					state: job_site_address.state,
					postcode: job_site_address.postcode,
					country: job_site_address.country,
				},
			};

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.post(`${pellaAPIURL}/web-ecommerce/api/v1/delivery`, data, { ...requestConfig, params: params });

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	const getManufacturingMethods = async () => {
		const userPostalCode = localStorage?.getItem('UserPostalCode');
		try {
			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const response = await axios.get(`${pellaAPIURL}/web-ecommerce/api/v1/manufacture-methods`, { ...requestConfig, params: params });

			return response.data;
		} catch (error) {
			return HandleAxiosError(error);
		}
	};

	return {
		updateCustomer,
		createAddress,
		getAddress,
		updateAddress,
		getTaxEstimate,
		addPromotion,
		deletePromotion,
		checkoutCart,
		getPayment,
		addPaypalPaymentMethod,
		recaptcha,
		createPaymentToken,
		getDeliveryOptions,
		getManufacturingMethods,
	};
};
